import { Components, Theme } from '@mui/material'

declare module '@mui/material/Avatar' {
  interface AvatarOwnProps {
    // Avatar doesn't have a color prop in MUI, so we add one
    color?: 'default' | 'futureTheme.gold' | 'futureTheme.mint' | 'futureTheme.plum' | 'futureTheme.lime'
    // The avatar's dimensions; the font-size scales with it
    size?: 16 | 20 | 24 | 32
  }

  // eslint-disable-next-line @typescript-eslint/no-empty-object-type
  interface AvatarPropsVariantOverrides {
    // TODO - use when no more usage of rounded variant
    // rounded: false
  }
}

export const MuiAvatar: Components<Omit<Theme, 'components'>>['MuiAvatar'] = {
  defaultProps: {
    variant: 'circular',
    color: 'default',
    size: 24,
  },
  styleOverrides: {
    root: ({ theme }) => ({
      borderWidth: theme.futureTheme.strokeWidth.default,
      borderStyle: 'solid',
    }),
  },
  variants: [
    // Size — width, height and font-size scale together
    {
      props: { size: 16 },
      style: ({ theme }) => ({
        width: 16,
        height: 16,
        ...theme.typography['futureTheme.extraSmall-semiBold'],
        fontSize: 6,
      }),
    },
    {
      props: { size: 20 },
      style: ({ theme }) => ({
        width: 20,
        height: 20,
        ...theme.typography['futureTheme.extraSmall-semiBold'],
        fontSize: 8,
      }),
    },
    {
      props: { size: 24 },
      style: ({ theme }) => ({
        width: 24,
        height: 24,
        ...theme.typography['futureTheme.extraSmall-semiBold'],
      }),
    },
    {
      props: { size: 32 },
      style: ({ theme }) => ({
        width: 32,
        height: 32,
        ...theme.typography['futureTheme.small-bold'],
      }),
    },
    // Shape
    {
      // Make rounded and square both show as the same thing for now, until we remove the rounded variant from usage
      props: (props) => props.variant === 'square' || props.variant === 'rounded',
      style: ({ theme }) => ({
        borderRadius: theme.shape['futureTheme.radius-sm'],
      }),
    },
    // Colour
    {
      props: { color: 'default' },
      style: ({ theme }) => ({
        borderWidth: 0,
        backgroundColor: theme.palette.futureTheme.bg.brand.default,
        color: theme.palette.futureTheme.text.brand.onBrand,
      }),
    },
    {
      props: { color: 'futureTheme.gold' },
      style: ({ theme }) => ({
        backgroundColor: theme.palette.futureTheme.bg.utility.gold.muted,
        borderColor: theme.palette.futureTheme.border.utility.gold.muted,
        color: theme.palette.futureTheme.text.utility.gold.default,
      }),
    },
    {
      props: { color: 'futureTheme.mint' },
      style: ({ theme }) => ({
        backgroundColor: theme.palette.futureTheme.bg.utility.mint.muted,
        borderColor: theme.palette.futureTheme.border.utility.mint.muted,
        color: theme.palette.futureTheme.text.utility.mint.default,
      }),
    },
    {
      props: { color: 'futureTheme.plum' },
      style: ({ theme }) => ({
        backgroundColor: theme.palette.futureTheme.bg.utility.plum.muted,
        borderColor: theme.palette.futureTheme.border.utility.plum.muted,
        color: theme.palette.futureTheme.text.utility.plum.default,
      }),
    },
    {
      props: { color: 'futureTheme.lime' },
      style: ({ theme }) => ({
        backgroundColor: theme.palette.futureTheme.bg.utility.lime.muted,
        borderColor: theme.palette.futureTheme.border.utility.lime.muted,
        color: theme.palette.futureTheme.text.utility.lime.default,
      }),
    },
  ],
}
